import React from "react";
import { Field, FieldProps } from "formik";
import { Autocomplete, TextField, Stack, Typography, Box } from "@mui/material";
import InfoMsg from "../InfoMsg";

type Option = {
  value: number | string;
  label: string;
};

export type AutocompleteProps = {
  value: number | string | undefined | null;
  options: Option[];
  placeholder: string;
  label?: string;
  name: string;
  required?: boolean;
  disabled?: boolean;
  color?:
    | "primary"
    | "info"
    | "error"
    | "secondary"
    | "success"
    | "warning"
    | undefined;
  variant?: "outlined" | "filled" | "standard" | undefined;
  freeSolo?: boolean;
  disableClearable?: boolean;
  updateBidStatus?: boolean;
  info?: string;
  multiple?: boolean;
  getOptionDisabled?: (option: Option) => boolean;
};

const FormikAutocomplete = (props: AutocompleteProps) => {
  const {
    name,
    options,
    label,
    placeholder,
    info,
    getOptionDisabled,
    required = false,
    color = "primary",
    variant = "outlined",
    disableClearable = true,
    updateBidStatus = false,
    ...otherProps
  } = props;

  const getValue = (value: number | string | undefined | null | string[]) => {
    if (!value) {
      return null;
    }
    if (otherProps.freeSolo) {
      return value.toString();
    }
    if (otherProps.multiple && Array.isArray(value)) {
      const result: Option[] = [];
      value.forEach((val) => {
        const userRec = options.find((rec) => rec.value === val);
        if (userRec) {
          result.push(userRec);
        }
      });
      return result;
    }
    const opt = options.find((rec) => rec.value === value);
    if (opt) {
      return opt;
    }
    return null;
  };
  const handleIsOptionEqualToValue = (option: Option, val: Option) => {
    return option.value === val.value;
  };

  const renderOptionTemplate = (props: object, option: Option) => {
    return (
      <li {...props} key={option.value}>
        {option.label}
      </li>
    );
  };

  return (
    <Field name={name}>
      {({ form, meta, field }: FieldProps) => {
        const { setFieldValue } = form;
        const { value } = field;
        const error = meta.touched && meta.error ? true : false;
        const helperText = error ? <>{meta.error}</> : null;
        const handleChange = (val: string | number | (string | Number)[]) => {
          if (updateBidStatus) {
            setFieldValue("projectBidStatus", val.toString());
          }
          setFieldValue(name, val);

          //resetting next approvals as per the order
          if(name === "buApproved" && val === "Not Selected") {
            setFieldValue("sbuApproved", "Not Selected");
            setFieldValue("sbgApproved", "Not Selected");
            setFieldValue("csgoApproved", "Not Selected");
          }
          if(name === "sbuApproved" && val === "Not Selected") {
            setFieldValue("sbgApproved", "Not Selected");
            setFieldValue("csgoApproved", "Not Selected");
          }
          if(name === "sbgApproved" && val === "Not Selected") {
            setFieldValue("csgoApproved", "Not Selected");
          }
        };

        const handleInputChange = (e: React.SyntheticEvent, val: string) => {
          if (otherProps.freeSolo) {
            handleChange(val);
          }
        };

        return (
          <Autocomplete
            disableClearable={disableClearable}
            id={name}
            fullWidth
            options={options}
            {...otherProps}
            {...field}
            getOptionDisabled={getOptionDisabled}
            isOptionEqualToValue={handleIsOptionEqualToValue}
            renderOption={renderOptionTemplate}
            value={getValue(value)}
            onChange={(e, option) => {
              if (option) {
                let selectedOption: string | number | (string | Number)[];
                if (Array.isArray(option)) {
                  selectedOption = option.map((val) => {
                    if (typeof val === "string") {
                      return val;
                    } else {
                      return val.value;
                    }
                  });
                } else {
                  if (typeof option === "string") {
                    selectedOption = option;
                  } else {
                    selectedOption = option.value;
                  }
                }
                handleChange(selectedOption);
              }
            }}
            onInputChange={handleInputChange}
            renderInput={(params) => (
              <Box>
                <Stack direction="row">
                  <Typography
                    component="span"
                    variant="subtitle2"
                    className={`${required ? "field-required" : ""}`}
                  >
                    {label}
                  </Typography>
                  {info && <InfoMsg info={info} />}
                </Stack>
                <TextField
                  {...params}
                  placeholder={placeholder}
                  color={color}
                  variant={variant}
                  error={error}
                  helperText={helperText}
                />
              </Box>
            )}
          />
        );
      }}
    </Field>
  );
};

export default FormikAutocomplete;
